import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { getTitleByGoalType, isUetBasedGoal, isTypeIncluedForEnhancedConversions } from '../../shared/contants';
import { SpecificGoalSettingsView } from './specific-goal-settings';
import { AdvancedGoalSettingsView } from './advanced-goal-settings';
import { getErrorMessage, getPreferredTag, processPayload, saveGoal } from '../../conversion-goals-repository';
import { validateSpecificView } from '../../shared/validation-util';
import { LearnMore } from '../../../../conversion-wizard-components/src/learn-more/learn-more';
import { endActivity } from '../../shared/util';

const GoalDetailStepUnstyled = (props) => {
  const {
    classes,
    exportUpdatedGoalObject,
    showNetworkCards,
  } = props;
  const noTagGoalTypes = ['ApplicationInstallGoal', 'InStoreTransactionGoal', 'OfflineConversionGoal', 'InStoreVisitGoal'];
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity } = wizardContext;
  const selectedGoalType = wizardContext.goalContext.Type;

  const isConversionGoalAttributionModelEnabled = isUetBasedGoal(wizardContext.goalContext);
  const { IsAmplifyEnabled } = wizardContext.permissions;

  if (isConversionGoalAttributionModelEnabled &&
    _.isUndefined(wizardContext.goalContext.AttributionModelType)) {
    if (IsAmplifyEnabled) {
      wizardContext.goalContext.AttributionModelType = 'LastTouch';
    } else {
      wizardContext.goalContext.AttributionModelType = 'Default';
    }
  }

  const [goalObj, setGoalObj] = useState(wizardContext.goalContext);
  const [associatedCampaigns, setAssociatedCampaigns] = useState(wizardContext && wizardContext.associatedCampaigns && wizardContext.associatedCampaigns.current);
  const [expandError, setExpandError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [footerEnabled, setFooterEnabled] = useState(true);
  const alertMessage = _TL_('We will turn auto-tagging of Microsoft Click ID on, as it is required for UET conversion goals. This will help to improve the accuracy of conversion tracking.');

  const showLinkedInCards = useMemo(() => showNetworkCards(selectedGoalType), [selectedGoalType, showNetworkCards]);

  const saveAssociatedCampaigns = useCallback(() => {
    wizardContext.dispatch({
      type: WIZARD_ACTIONS.UPDATE_ASSOCIATED_CAMPAIGNS_DATA,
      associatedCampaigns: {
        current: associatedCampaigns,
      },
    });
  }, [wizardContext, associatedCampaigns]);

  const allowEnhancedConversions = useMemo(() => {
    const { permissions } = wizardContext;
    const enhancedConversionType = isTypeIncluedForEnhancedConversions({
      goalType: selectedGoalType,
      goalValueType: goalObj.GoalValueType,
    });
    return permissions && permissions.dynamic && permissions.dynamic.EnhancedConversions && enhancedConversionType;
  }, [goalObj.GoalValueType, selectedGoalType, wizardContext]);

  const updateSpecificSettings = useCallback((updatedProps) => {
    setGoalObj({
      ...goalObj,
      ...updatedProps,
    });
  }, [goalObj]);

  const updateAdvancedSettings = useCallback((updatedProps) => {
    setGoalObj({
      ...goalObj,
      ...updatedProps,
    });
  }, [goalObj]);

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      notificationId = notificationApi.showWarning(notificationError);
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const validateLookbackWindow = useCallback(val => !_.isNaN(val) && val > 0, []);

  const validateAdvancedSettings = useCallback(
    () => validateLookbackWindow(goalObj.LookbackWindowInMinutes) && (validateLookbackWindow(goalObj.ViewThroughLookbackWindowinMinutes) || goalObj.ViewThroughLookbackWindowinMinutes === null),
    [goalObj.LookbackWindowInMinutes, goalObj.ViewThroughLookbackWindowinMinutes, validateLookbackWindow]
  );

  const validateSpecificSettings = useCallback(() => {
    // Name, GoalValueType, GoalValue, CurrencyCode
    if (_.isUndefined(goalObj.Name)) {
      return false;
    }
    if (_.isUndefined(goalObj.GoalValueType)) {
      return false;
    }
    if (goalObj.GoalValueType !== 'NoValue' && _.isUndefined(goalObj.GoalValue)) {
      return false;
    }
    // specific view
    return validateSpecificView(goalObj);
  }, [goalObj]);

  useMemo(() => {
    if (exportUpdatedGoalObject !== _.noop) {
      exportUpdatedGoalObject(goalObj, validateAdvancedSettings() && validateSpecificSettings());
    }
  }, [exportUpdatedGoalObject, goalObj, validateAdvancedSettings, validateSpecificSettings]);

  const skipTagSelectionStep = wizardContext.goalContext.Type && noTagGoalTypes.indexOf(wizardContext.goalContext.Type) > -1;

  const [advSettingHasError, setAdvSettingHasError] = useState(false);
  const allowNext = useMemo(() => {
    if (expandError || showError || advSettingHasError) {
      return false;
    }
    if (_.isUndefined(goalObj.Name)) {
      return false;
    }
    if (_.isUndefined(goalObj.GoalValue)) {
      return false;
    }
    if (_.isUndefined(goalObj.CurrencyCode)) {
      return false;
    }
    if (_.isUndefined(goalObj.GoalValueType)) {
      return false;
    }
    return true;
  }, [goalObj.Name, goalObj.GoalValue, goalObj.CurrencyCode, goalObj.GoalValueType, advSettingHasError, expandError, showError]);


  const saveAndNext = useCallback(() => {
    const validateAdvancedResult = validateAdvancedSettings();
    if (!validateAdvancedResult) {
      setExpandError(true);
    }
    const validateSpecificResult = validateSpecificSettings();
    if (!validateSpecificResult) {
      setShowError(true);
    }

    if (!validateAdvancedResult || !validateSpecificResult) {
      return;
    }

    saveAssociatedCampaigns();

    if (skipTagSelectionStep) {
      saveGoal(processPayload(goalObj, wizardContext.permissions), wizardContext)
        .then((response) => {
          endActivity(logActivity);
          wizardContext.dispatch({
            type: allowEnhancedConversions ?
              WIZARD_ACTIONS.NAVIGATE_TO_ENHANCED_CONV :
              WIZARD_ACTIONS.NAVIGATE_TO_LAST_STEP,
            data: {
              ...goalObj,
              Id: response.Id,
            },
            switchToEdit: true,
          });
        })
        .catch((response) => {
          setFooterEnabled(true);
          setNotificationError(getErrorMessage(response, i18n));
        });
      setFooterEnabled(false);
    } else {
      wizardContext.tagPromise.then((tags) => {
        const tag = _.isEmpty(wizardContext.goalContext.Tag) ? getPreferredTag(tags) : wizardContext.goalContext.Tag;
        const updatedTag = tag ? { Tag: tag } : {};
        endActivity(logActivity);
        if (tag && tag.isAutoCreated) {
          const goalToSave = {
            ...goalObj,
            Tag: {
              Id: tag.Id,
            },
          };
          saveGoal(processPayload(goalToSave, wizardContext.permissions), wizardContext).then((response) => {
            wizardContext.dispatch({
              type: allowEnhancedConversions ?
                WIZARD_ACTIONS.NAVIGATE_TO_ENHANCED_CONV :
                WIZARD_ACTIONS.NAVIGATE_WITH_TAGINSTALL_OPTIONS,
              data: {
                ...goalToSave,
                Id: response.Id,
                ...updatedTag,
                tags,
              },
              tagInstallOption: 'no',
            });
          })
            .catch((response) => {
              setFooterEnabled(true);
              setNotificationError(getErrorMessage(response, i18n));
            });
        } else {
          wizardContext.dispatch({
            type: allowEnhancedConversions ?
              WIZARD_ACTIONS.NAVIGATE_TO_ENHANCED_CONV :
              WIZARD_ACTIONS.WIZARD_SAVE_AND_NAVIGATE_TAG_SELECTION,
            data: {
              ...goalObj,
              ...updatedTag,
              tags,
            },
          });
        }
      }).catch((tagResponse) => {
        logActivity.error(`Could not retrieve tags for goal creation:${JSON.stringify(tagResponse)}`);
        notificationApi.showWarning(i18n.getString(_TL_('Could not fetch UET tags for your account. Please try again later')));
      });
    }
  }, [validateAdvancedSettings, validateSpecificSettings, saveAssociatedCampaigns, allowEnhancedConversions, skipTagSelectionStep, wizardContext, goalObj, logActivity, i18n]);

  const resetShowError = useCallback(() => setShowError(false), []);
  const resetExpandError = useCallback(() => setExpandError(false), []);

  const primaryBtnText = useMemo(() => {
    if (skipTagSelectionStep) {
      return i18n.getString(_TL_('Save and next'));
    }
    return i18n.getString(_TL_('Next'));
  }, [skipTagSelectionStep, i18n]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Back')),
    },
    {
      type: 'Primary',
      onClick: saveAndNext,
      isEnabled: footerEnabled && allowNext,
      text: primaryBtnText,
    },
    {
      type: 'Cancel',
      onClick: wizardContext.cancel,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Cancel')),
    }],
  }), [wizardContext.goBack, wizardContext.cancel, footerEnabled, i18n, saveAndNext, primaryBtnText, allowNext]);

  return (
    <StepContainer
      title={i18n.getString(getTitleByGoalType(wizardContext.isEditScenario, selectedGoalType))}
      step="goal-details"
      footerConfig={footerConfig}
    >
      <div className={classes.contentBlock}>
        <SpecificGoalSettingsView
          selectedGoalType={selectedGoalType}
          goalObj={goalObj}
          updateGoalContext={updateSpecificSettings}
          showError={showError}
          resetShowError={resetShowError}
          permissions={wizardContext.permissions}
        />
        <hr />
        <AdvancedGoalSettingsView
          selectedGoalType={selectedGoalType}
          isConversionGoalAttributionModelEnabled={isConversionGoalAttributionModelEnabled}
          updateGoalContext={updateAdvancedSettings}
          expandError={expandError}
          resetExpandError={resetExpandError}
          setAdvSettingHasError={setAdvSettingHasError}
          // LinkedIn specific props
          showLinkedInCards={showLinkedInCards}
          goalHasAssociatedCampaigns={!_.isEmpty(associatedCampaigns)}
          associatedCampaigns={associatedCampaigns}
          onAssociatedCampaignsChange={setAssociatedCampaigns}
        />
        {/* alert banner */}
        <div className="alert alert-info">
          <span className="alert-image" />
          <span>
            {i18n.getString(alertMessage)}
            <LearnMore i18n={i18n} topicId="app54501" ariaLabel={i18n.getString(alertMessage)} />
          </span>
        </div>
      </div>
    </StepContainer>
  );
};

GoalDetailStepUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  exportUpdatedGoalObject: PropTypes.func,
  showNetworkCards: PropTypes.func,
};

GoalDetailStepUnstyled.defaultProps = {
  exportUpdatedGoalObject: _.noop,
  showNetworkCards: _.noop,
};

export const GoalDetailsStep = withPerfMarker(withDefaultStyles(GoalDetailStepUnstyled, getStepStyles));
